import { fetchCurrentUser } from './user.duck';
import {
  getTaxonomyNodesApi,
  getTaxonomyMappingsApi,
  upsertTaxonomyMappingApi,
  getAttributeMappingsApi,
  upsertAttributeMappingsApi,
  getShopifyProductsApi,
  importShopifyProductsApi,
  getShopifyFeeReportApi,
  getShopifyBillingStatusApi,
  onFindCategoryCollection,
} from '../util/api';
import { basicTables } from '../containers/EditListingPage/EditListingPage.duck';

// ================ Action types ================ //

const ns = 'app/ShopifyOnboardingPage';

export const FETCH_TAXONOMY_NODES_REQUEST = `${ns}/FETCH_TAXONOMY_NODES_REQUEST`;
export const FETCH_TAXONOMY_NODES_SUCCESS = `${ns}/FETCH_TAXONOMY_NODES_SUCCESS`;
export const FETCH_TAXONOMY_NODES_ERROR = `${ns}/FETCH_TAXONOMY_NODES_ERROR`;

export const FETCH_TAXONOMY_MAPPINGS_REQUEST = `${ns}/FETCH_TAXONOMY_MAPPINGS_REQUEST`;
export const FETCH_TAXONOMY_MAPPINGS_SUCCESS = `${ns}/FETCH_TAXONOMY_MAPPINGS_SUCCESS`;
export const FETCH_TAXONOMY_MAPPINGS_ERROR = `${ns}/FETCH_TAXONOMY_MAPPINGS_ERROR`;

export const SAVE_TAXONOMY_MAPPING_REQUEST = `${ns}/SAVE_TAXONOMY_MAPPING_REQUEST`;
export const SAVE_TAXONOMY_MAPPING_SUCCESS = `${ns}/SAVE_TAXONOMY_MAPPING_SUCCESS`;
export const SAVE_TAXONOMY_MAPPING_ERROR = `${ns}/SAVE_TAXONOMY_MAPPING_ERROR`;

export const FETCH_ATTRIBUTE_MAPPINGS_REQUEST = `${ns}/FETCH_ATTRIBUTE_MAPPINGS_REQUEST`;
export const FETCH_ATTRIBUTE_MAPPINGS_SUCCESS = `${ns}/FETCH_ATTRIBUTE_MAPPINGS_SUCCESS`;
export const FETCH_ATTRIBUTE_MAPPINGS_ERROR = `${ns}/FETCH_ATTRIBUTE_MAPPINGS_ERROR`;

export const SAVE_ATTRIBUTE_MAPPING_REQUEST = `${ns}/SAVE_ATTRIBUTE_MAPPING_REQUEST`;
export const SAVE_ATTRIBUTE_MAPPING_SUCCESS = `${ns}/SAVE_ATTRIBUTE_MAPPING_SUCCESS`;
export const SAVE_ATTRIBUTE_MAPPING_ERROR = `${ns}/SAVE_ATTRIBUTE_MAPPING_ERROR`;

export const FETCH_CATEGORIES_REQUEST = `${ns}/FETCH_CATEGORIES_REQUEST`;
export const FETCH_CATEGORIES_SUCCESS = `${ns}/FETCH_CATEGORIES_SUCCESS`;
export const FETCH_CATEGORIES_ERROR = `${ns}/FETCH_CATEGORIES_ERROR`;

export const FETCH_PRODUCTS_REQUEST = `${ns}/FETCH_PRODUCTS_REQUEST`;
export const FETCH_PRODUCTS_SUCCESS = `${ns}/FETCH_PRODUCTS_SUCCESS`;
export const FETCH_PRODUCTS_ERROR = `${ns}/FETCH_PRODUCTS_ERROR`;

export const IMPORT_PRODUCTS_REQUEST = `${ns}/IMPORT_PRODUCTS_REQUEST`;
export const IMPORT_PRODUCTS_SUCCESS = `${ns}/IMPORT_PRODUCTS_SUCCESS`;
export const IMPORT_PRODUCTS_ERROR = `${ns}/IMPORT_PRODUCTS_ERROR`;

export const FETCH_FEE_EVENTS_REQUEST = `${ns}/FETCH_FEE_EVENTS_REQUEST`;
export const FETCH_FEE_EVENTS_SUCCESS = `${ns}/FETCH_FEE_EVENTS_SUCCESS`;
export const FETCH_FEE_EVENTS_ERROR = `${ns}/FETCH_FEE_EVENTS_ERROR`;

export const FETCH_BILLING_STATUS_REQUEST = `${ns}/FETCH_BILLING_STATUS_REQUEST`;
export const FETCH_BILLING_STATUS_SUCCESS = `${ns}/FETCH_BILLING_STATUS_SUCCESS`;
export const FETCH_BILLING_STATUS_ERROR = `${ns}/FETCH_BILLING_STATUS_ERROR`;

// ================ Reducer ================ //

const initialState = {
  // Map Categories tab — taxonomy nodes (distinct Shopify taxonomy GIDs in user's products)
  taxonomyNodes: [],
  taxonomyNodesInProgress: false,
  taxonomyNodesError: null,

  // Map Categories tab — saved taxonomy → marketplace category mappings
  taxonomyMappings: [],
  taxonomyMappingsInProgress: false,
  taxonomyMappingsError: null,

  saveTaxonomyMappingInProgress: false,
  saveTaxonomyMappingError: null,

  // Map Categories tab — saved option → dynamic field value mappings
  attributeMappings: [],
  attributeMappingsInProgress: false,
  attributeMappingsError: null,

  saveAttributeMappingInProgress: false,
  saveAttributeMappingError: null,

  // Marketplace category tree + dynamic fields (loaded once, shared by both tabs)
  categories: [],
  subCategories: [],
  subSubCategories: [],
  dynamicFields: [],
  categoriesInProgress: false,
  categoriesError: null,

  // Import Products tab
  products: [],
  productsPagination: null,
  productsInProgress: false,
  productsError: null,

  importInProgress: false,
  importResults: null,
  importError: null,

  // Shopify Fee tab
  feeEvents: [],
  feeEventsPagination: null,
  feeEventsInProgress: false,
  feeEventsError: null,

  billing: null,
  billingStatusInProgress: false,
  billingStatusError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TAXONOMY_NODES_REQUEST:
      return { ...state, taxonomyNodesInProgress: true, taxonomyNodesError: null };
    case FETCH_TAXONOMY_NODES_SUCCESS:
      return { ...state, taxonomyNodesInProgress: false, taxonomyNodes: payload };
    case FETCH_TAXONOMY_NODES_ERROR:
      return { ...state, taxonomyNodesInProgress: false, taxonomyNodesError: payload };

    case FETCH_TAXONOMY_MAPPINGS_REQUEST:
      return { ...state, taxonomyMappingsInProgress: true, taxonomyMappingsError: null };
    case FETCH_TAXONOMY_MAPPINGS_SUCCESS:
      return { ...state, taxonomyMappingsInProgress: false, taxonomyMappings: payload };
    case FETCH_TAXONOMY_MAPPINGS_ERROR:
      return { ...state, taxonomyMappingsInProgress: false, taxonomyMappingsError: payload };

    case SAVE_TAXONOMY_MAPPING_REQUEST:
      return { ...state, saveTaxonomyMappingInProgress: true, saveTaxonomyMappingError: null };
    case SAVE_TAXONOMY_MAPPING_SUCCESS:
      return {
        ...state,
        saveTaxonomyMappingInProgress: false,
        // Merge the saved mapping into the local list
        taxonomyMappings: state.taxonomyMappings.some(
          m => m.shopify_taxonomy_id === payload.shopify_taxonomy_id
        )
          ? state.taxonomyMappings.map(m =>
              m.shopify_taxonomy_id === payload.shopify_taxonomy_id ? payload : m
            )
          : [...state.taxonomyMappings, payload],
      };
    case SAVE_TAXONOMY_MAPPING_ERROR:
      return { ...state, saveTaxonomyMappingInProgress: false, saveTaxonomyMappingError: payload };

    case FETCH_ATTRIBUTE_MAPPINGS_REQUEST:
      return { ...state, attributeMappingsInProgress: true, attributeMappingsError: null };
    case FETCH_ATTRIBUTE_MAPPINGS_SUCCESS:
      return { ...state, attributeMappingsInProgress: false, attributeMappings: payload };
    case FETCH_ATTRIBUTE_MAPPINGS_ERROR:
      return { ...state, attributeMappingsInProgress: false, attributeMappingsError: payload };

    case SAVE_ATTRIBUTE_MAPPING_REQUEST:
      return { ...state, saveAttributeMappingInProgress: true, saveAttributeMappingError: null };
    case SAVE_ATTRIBUTE_MAPPING_SUCCESS:
      return { ...state, saveAttributeMappingInProgress: false };
    case SAVE_ATTRIBUTE_MAPPING_ERROR:
      return {
        ...state,
        saveAttributeMappingInProgress: false,
        saveAttributeMappingError: payload,
      };

    case FETCH_CATEGORIES_REQUEST:
      return { ...state, categoriesInProgress: true, categoriesError: null };
    case FETCH_CATEGORIES_SUCCESS:
      return {
        ...state,
        categoriesInProgress: false,
        categories: payload.categories,
        subCategories: payload.subCategories,
        subSubCategories: payload.subSubCategories,
        dynamicFields: payload.dynamicFields,
      };
    case FETCH_CATEGORIES_ERROR:
      return { ...state, categoriesInProgress: false, categoriesError: payload };

    case FETCH_PRODUCTS_REQUEST:
      return { ...state, productsInProgress: true, productsError: null };
    case FETCH_PRODUCTS_SUCCESS:
      return {
        ...state,
        productsInProgress: false,
        products: payload.products,
        productsPagination: payload.pagination,
      };
    case FETCH_PRODUCTS_ERROR:
      return { ...state, productsInProgress: false, productsError: payload };

    case IMPORT_PRODUCTS_REQUEST:
      return { ...state, importInProgress: true, importError: null, importResults: null };
    case IMPORT_PRODUCTS_SUCCESS:
      return { ...state, importInProgress: false, importResults: payload };
    case IMPORT_PRODUCTS_ERROR:
      return { ...state, importInProgress: false, importError: payload };

    case FETCH_FEE_EVENTS_REQUEST:
      return { ...state, feeEventsInProgress: true, feeEventsError: null };
    case FETCH_FEE_EVENTS_SUCCESS:
      return {
        ...state,
        feeEventsInProgress: false,
        feeEvents: payload.events,
        feeEventsPagination: payload.pagination,
      };
    case FETCH_FEE_EVENTS_ERROR:
      return { ...state, feeEventsInProgress: false, feeEventsError: payload };

    case FETCH_BILLING_STATUS_REQUEST:
      return { ...state, billingStatusInProgress: true, billingStatusError: null };
    case FETCH_BILLING_STATUS_SUCCESS:
      return { ...state, billingStatusInProgress: false, billing: payload };
    case FETCH_BILLING_STATUS_ERROR:
      return { ...state, billingStatusInProgress: false, billingStatusError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchTaxonomyNodesRequest = () => ({ type: FETCH_TAXONOMY_NODES_REQUEST });
const fetchTaxonomyNodesSuccess = data => ({ type: FETCH_TAXONOMY_NODES_SUCCESS, payload: data });
const fetchTaxonomyNodesError = err => ({ type: FETCH_TAXONOMY_NODES_ERROR, payload: err });

const fetchTaxonomyMappingsRequest = () => ({ type: FETCH_TAXONOMY_MAPPINGS_REQUEST });
const fetchTaxonomyMappingsSuccess = data => ({
  type: FETCH_TAXONOMY_MAPPINGS_SUCCESS,
  payload: data,
});
const fetchTaxonomyMappingsError = err => ({
  type: FETCH_TAXONOMY_MAPPINGS_ERROR,
  payload: err,
});

const saveTaxonomyMappingRequest = () => ({ type: SAVE_TAXONOMY_MAPPING_REQUEST });
const saveTaxonomyMappingSuccess = data => ({ type: SAVE_TAXONOMY_MAPPING_SUCCESS, payload: data });
const saveTaxonomyMappingError = err => ({ type: SAVE_TAXONOMY_MAPPING_ERROR, payload: err });

const fetchAttributeMappingsRequest = () => ({ type: FETCH_ATTRIBUTE_MAPPINGS_REQUEST });
const fetchAttributeMappingsSuccess = data => ({
  type: FETCH_ATTRIBUTE_MAPPINGS_SUCCESS,
  payload: data,
});
const fetchAttributeMappingsError = err => ({
  type: FETCH_ATTRIBUTE_MAPPINGS_ERROR,
  payload: err,
});

const saveAttributeMappingRequest = () => ({ type: SAVE_ATTRIBUTE_MAPPING_REQUEST });
const saveAttributeMappingSuccess = () => ({ type: SAVE_ATTRIBUTE_MAPPING_SUCCESS });
const saveAttributeMappingError = err => ({ type: SAVE_ATTRIBUTE_MAPPING_ERROR, payload: err });

const fetchCategoriesRequest = () => ({ type: FETCH_CATEGORIES_REQUEST });
const fetchCategoriesSuccess = data => ({ type: FETCH_CATEGORIES_SUCCESS, payload: data });
const fetchCategoriesError = err => ({ type: FETCH_CATEGORIES_ERROR, payload: err });

const fetchProductsRequest = () => ({ type: FETCH_PRODUCTS_REQUEST });
const fetchProductsSuccess = data => ({ type: FETCH_PRODUCTS_SUCCESS, payload: data });
const fetchProductsError = err => ({ type: FETCH_PRODUCTS_ERROR, payload: err });

const importProductsRequest = () => ({ type: IMPORT_PRODUCTS_REQUEST });
const importProductsSuccess = data => ({ type: IMPORT_PRODUCTS_SUCCESS, payload: data });
const importProductsError = err => ({ type: IMPORT_PRODUCTS_ERROR, payload: err });

const fetchFeeEventsRequest = () => ({ type: FETCH_FEE_EVENTS_REQUEST });
const fetchFeeEventsSuccess = data => ({ type: FETCH_FEE_EVENTS_SUCCESS, payload: data });
const fetchFeeEventsError = err => ({ type: FETCH_FEE_EVENTS_ERROR, payload: err });

const fetchBillingStatusRequest = () => ({ type: FETCH_BILLING_STATUS_REQUEST });
const fetchBillingStatusSuccess = data => ({ type: FETCH_BILLING_STATUS_SUCCESS, payload: data });
const fetchBillingStatusError = err => ({ type: FETCH_BILLING_STATUS_ERROR, payload: err });

// ================ Thunks ================ //

export const fetchTaxonomyNodes = userId => dispatch => {
  dispatch(fetchTaxonomyNodesRequest());
  return getTaxonomyNodesApi({ userId })
    .then(res => dispatch(fetchTaxonomyNodesSuccess(res.nodes || [])))
    .catch(e => dispatch(fetchTaxonomyNodesError(e?.message || 'Failed to load taxonomy nodes')));
};

export const fetchTaxonomyMappings = userId => dispatch => {
  dispatch(fetchTaxonomyMappingsRequest());
  return getTaxonomyMappingsApi({ userId })
    .then(res => dispatch(fetchTaxonomyMappingsSuccess(res.mappings || [])))
    .catch(e =>
      dispatch(fetchTaxonomyMappingsError(e?.message || 'Failed to load taxonomy mappings'))
    );
};

export const saveTaxonomyMapping = mappingPayload => dispatch => {
  dispatch(saveTaxonomyMappingRequest());
  return upsertTaxonomyMappingApi(mappingPayload)
    .then(res => dispatch(saveTaxonomyMappingSuccess(res.mapping)))
    .catch(e =>
      dispatch(saveTaxonomyMappingError(e?.message || 'Failed to save taxonomy mapping'))
    );
};

export const fetchAttributeMappings = userId => dispatch => {
  dispatch(fetchAttributeMappingsRequest());
  return getAttributeMappingsApi({ userId })
    .then(res => dispatch(fetchAttributeMappingsSuccess(res.mappings || [])))
    .catch(e =>
      dispatch(fetchAttributeMappingsError(e?.message || 'Failed to load attribute mappings'))
    );
};

export const saveAttributeMappings = payload => (dispatch, getState) => {
  dispatch(saveAttributeMappingRequest());
  return upsertAttributeMappingsApi(payload)
    .then(() => {
      dispatch(saveAttributeMappingSuccess());
      // Reload attribute mappings so UI reflects the saved state
      const userId = getState().user?.currentUser?.id?.uuid;
      if (userId) dispatch(fetchAttributeMappings(userId));
    })
    .catch(e =>
      dispatch(saveAttributeMappingError(e?.message || 'Failed to save attribute mappings'))
    );
};

// Fetches all three category levels + dynamic fields in one call using the
// existing findCategoryCollection endpoint.
export const fetchMarketplaceCategories = () => dispatch => {
  dispatch(fetchCategoriesRequest());
  const activeFilter = { isActive: true, isDelete: false };
  return onFindCategoryCollection(basicTables)
    .then(res => {
      console.log('fetchMarketplaceCategories res', res);
      return dispatch(
        fetchCategoriesSuccess({
          categories: res?.CATEGORY || [],
          subCategories: res?.SUB_CATEGORY || [],
          subSubCategories: res?.SUB_SUB_CATEGORY || [],
          dynamicFields: res?.DYNAMIC_FIELDS_SUB_SUB_CAT || [],
        })
      );
    })
    .catch(e => dispatch(fetchCategoriesError(e?.message || 'Failed to load categories')));
};

export const fetchShopifyProducts = ({
  userId,
  page = 1,
  perPage = 20,
  importStatus,
} = {}) => dispatch => {
  dispatch(fetchProductsRequest());
  return getShopifyProductsApi({ userId, page, perPage, importStatus })
    .then(res =>
      dispatch(fetchProductsSuccess({ products: res.products || [], pagination: res.pagination }))
    )
    .catch(e => dispatch(fetchProductsError(e?.message || 'Failed to load products')));
};

export const fetchShopifyFeeEvents = ({
  userId,
  adminReport = false,
  page = 1,
  perPage = 10,
  month,
  year,
  monthYears,
  checkoutStatus,
  searchKeyword,
  reportRole,
} = {}) => dispatch => {
  dispatch(fetchFeeEventsRequest());
  const normalizedReportRole = reportRole || 'seller';
  return getShopifyFeeReportApi({
    userId,
    adminReport,
    page,
    perPage,
    month,
    year,
    monthYears,
    checkoutStatus,
    searchKeyword,
    reportRole: normalizedReportRole,
  })
    .then(res =>
      dispatch(
        fetchFeeEventsSuccess({ events: res.events || [], pagination: res.pagination || null })
      )
    )
    .catch(e => dispatch(fetchFeeEventsError(e?.message || 'Failed to load Shopify fees')));
};

export const fetchShopifyBillingStatus = userId => dispatch => {
  dispatch(fetchBillingStatusRequest());
  return getShopifyBillingStatusApi({ userId })
    .then(res => dispatch(fetchBillingStatusSuccess(res.billing || null)))
    .catch(e =>
      dispatch(fetchBillingStatusError(e?.message || e?.error || 'Failed to load billing status'))
    );
};


export const importProducts = (userId, productIds) => dispatch => {
  dispatch(importProductsRequest());
  return importShopifyProductsApi({ userId, productIds })
    .then(res => dispatch(importProductsSuccess(res.results || [])))
    .catch(e => dispatch(importProductsError(e?.message || 'Import failed')));
};

export const toggleProductImport = (userId, productId, enable) => dispatch => {
  if (enable) {
    dispatch(importProductsRequest());
    return importShopifyProductsApi({ userId, productIds: [productId] })
      .then(res => {
        dispatch(importProductsSuccess(res.results || []));
        dispatch(fetchShopifyProducts({ userId }));
      })
      .catch(e => dispatch(importProductsError(e?.message || 'Import failed')));
  } else {
    // Draft/disable path — calls /shopify/products/set-draft when implemented
    console.warn('[toggleProductImport] Draft path not yet implemented for productId:', productId);
  }
};

// ================ loadData ================ //
// Called by the route before the page renders.

export const loadData = (params, search) => (dispatch, getState) => {
  return dispatch(fetchCurrentUser()).then(() => {
    const currentUser = getState().user.currentUser;
    const userId = currentUser?.id?.uuid;
    if (!userId) return;

    const tab = params?.tab;
    // Always load the marketplace category tree (shared by both tabs)
    const baseLoads = [
      dispatch(fetchMarketplaceCategories()),
      dispatch(fetchShopifyBillingStatus(userId)),
    ];

    if (tab === 'shopify-fee') {
      return Promise.all([
        ...baseLoads,
        dispatch(fetchShopifyFeeEvents({ userId, checkoutStatus: 'COMPLETED' })),
      ]);
    }

    if (tab === 'map-categories') {
      return Promise.all([
        ...baseLoads,
        dispatch(fetchTaxonomyNodes(userId)),
        dispatch(fetchTaxonomyMappings(userId)),
        dispatch(fetchAttributeMappings(userId)),
      ]);
    }

    if (tab === 'import-products') {
      return Promise.all([
        ...baseLoads,
        dispatch(fetchTaxonomyMappings(userId)),
        dispatch(fetchShopifyProducts({ userId })),
      ]);
    }

    return Promise.all(baseLoads);
  });
};
